import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import analytics from "core/utils/analytics";
import { useQuery } from "@apollo/client";
import { BookQuery } from "arena/queries";
import BookHelmet from "./BookHelmet";
import ErrorScreen from "core/components/ErrorScreen";
import NoMatchScreen from "arena/components/NoMatchScreen";
import { Navigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ThumbnailCardMedia from "arena/components/ThumbnailCardMedia";
import Chip from "@mui/material/Chip";
import TagList from "arena/components/TagList";
import { CloseRouteDialogButton } from "arena/components/RouteDialog";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import VisibilityIcon from "@mui/icons-material/Visibility";
import useProductDialog from "arena/hooks/useProductDialog";
import ProductDialog from "arena/components/ProductDialog";
import BookTableOfContentsDialog from "./BookTableOfContentsDialog";
import useIsMobile from "arena/hooks/useIsMobile";
import PaymentButtons from "core/components/PaymentButtons";
import { useArena } from "core/components/ArenaProvider";
import PaymentsIcon from "@mui/icons-material/Payments";
import { isTelegramMiniApp } from "core/utils/telegram";
import ShareButton from "arena/components/ShareButton";
import SubscribeContentButton from "core/components/SubscribeContentButton";
import { useViewer } from "core/components/ViewerProvider";
import AgeConfirmationDialog from "./AgeConfirmationDialog";
import BookDownloadButton from "./BookDownloadButton";

// Компонент для вертикального превью книги (пропорции 2:3)
const StyledThumbnailCardMedia = styled(ThumbnailCardMedia)(({ theme }) => ({
  width: "100%",
  position: "relative",
  height: 0,
  paddingTop: "150%", // 2:3 пропорция (3/2 = 1.5 = 150%)
  backgroundColor: theme.palette.mode === "dark" ? "black" : "rgba(0,0,0,0.01)",
}));

export default function Book({ params: { contentId } }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("arena");
  const { state, dispatch } = useProductDialog({});
  const isMobile = useIsMobile();
  const [tocDialogOpen, setTocDialogOpen] = useState(false);
  const [ageConfirmOpen, setAgeConfirmOpen] = useState(false);
  const [pendingReadTarget, setPendingReadTarget] = useState(null);
  const arena = useArena();
  const viewer = useViewer();

  const onCompleted = (data) => {
    if (data.content) {
      analytics.send({
        hitType: "pageview",
        page: location.pathname,
        title: data.content.title,
      });
    }
  };

  const { data, loading, refetch, error } = useQuery(BookQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      id: contentId,
    },
    onCompleted,
  });

  const content = data?.content;
  const metadata =
    content?.metadataFields?.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: field.value,
      }),
      {}
    ) || {};

  const buildReaderLocation = useCallback(
    (target = null) => {
      const searchParams = new URLSearchParams();

      if (target?.foliateHref) {
        searchParams.set("href", target.foliateHref);
      } else if (target?.characterPosition !== undefined) {
        searchParams.set("pos", target.characterPosition);
      }

      const search = searchParams.toString();

      return {
        pathname: `/book/${contentId}/read`,
        search: search ? `?${search}` : "",
      };
    },
    [contentId]
  );

  const navigateToReader = useCallback(
    (target = null) => {
      navigate(buildReaderLocation(target));
    },
    [buildReaderLocation, navigate]
  );

  const openReaderSignIn = useCallback(
    (target = null) => {
      const readerLocation = buildReaderLocation(target);
      const searchParams = new URLSearchParams(readerLocation.search);

      searchParams.set("signin", "1");

      navigate({
        pathname: readerLocation.pathname,
        search: `?${searchParams.toString()}`,
      });
    },
    [buildReaderLocation, navigate]
  );

  const handleReadIntent = useCallback(
    (target = null) => {
      if (!content?.access?.token) {
        return;
      }

      if (!viewer) {
        openReaderSignIn(target);
        return;
      }

      const age = content.ageRestrictions;
      const needsConfirm = age >= 18 && (viewer.ageConfirmed ?? 0) < age;

      if (needsConfirm) {
        setPendingReadTarget(target);
        setAgeConfirmOpen(true);
        return;
      }

      navigateToReader(target);
    },
    [
      content?.access?.token,
      content?.ageRestrictions,
      navigateToReader,
      openReaderSignIn,
      viewer,
    ]
  );

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (!content && !loading) {
    return <NoMatchScreen />;
  }

  if (content && content.type !== "BOOK") {
    return (
      <Navigate
        to={content.siteUrl.replace("book", content.type.toLowerCase())}
      />
    );
  }

  return (
    <>
      {content && <BookHelmet content={content} />}

      {content && (
        <Container maxWidth="md" sx={{ p: 0 }}>
          <Card
            sx={{
              backgroundColor: "rgba(255,255,255,0.03)",
              position: "relative",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "flex-start", p: 2, pb: 1 }}
            >
              <CloseRouteDialogButton />
              <CardHeader
                title={content.title}
                titleTypographyProps={{
                  sx: {
                    mb: 2,
                    ml: isTelegramMiniApp ? 0 : 7,
                  },
                }}
                sx={{ flex: 1, p: 0 }}
              />
            </Box>

            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                {/* Левая часть - картинка */}
                <Grid item xs={12} md={3}>
                  <Box sx={{ position: "relative" }}>
                    {content.verticalThumbnails?.length > 0 ||
                    content.thumbnails?.length > 0 ? (
                      <StyledThumbnailCardMedia
                        thumbnails={
                          content.verticalThumbnails?.length > 0
                            ? content.verticalThumbnails
                            : content.thumbnails
                        }
                        alt={content.title}
                        size="MAXRES"
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: 0,
                          paddingTop: "150%",
                          backgroundColor: "rgba(0,0,0,0.1)",
                          borderRadius: 1,
                          position: "relative",
                        }}
                      />
                    )}
                    {content.ageRestrictions > 0 && (
                      <Chip
                        label={`${content.ageRestrictions}+`}
                        size="small"
                        color="primary"
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                        }}
                      />
                    )}
                  </Box>
                  {content.sku && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1 }}
                    >
                      {t("book.sku", { sku: content.sku })}
                    </Typography>
                  )}
                </Grid>

                {/* Правая часть - описание и контролы */}
                <Grid item xs={12} md={9}>
                  {content.tags.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <TagList tags={content.tags} />
                    </Box>
                  )}

                  <Box
                    sx={{ mb: 2, "& p:first-of-type": { marginTop: 0 } }}
                    dangerouslySetInnerHTML={{
                      __html: content.premiumDescription || content.description,
                    }}
                  />
                  {metadata.haveDrugs === "true" && (
                    <Alert variant="outlined" severity="warning" sx={{ mb: 2 }}>
                      {t("book.haveDrugs")}
                    </Alert>
                  )}
                  {metadata.haveFoulLanguage === "true" && (
                    <Alert variant="outlined" severity="warning" sx={{ mb: 2 }}>
                      {t("book.haveFoulLanguage")}
                    </Alert>
                  )}

                  {/* Прогресс чтения */}
                  {content.bookMetadata?.userBook && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t("book.readingProgress", "Прогресс чтения")}:{" "}
                        {Math.round(
                          content.bookMetadata.userBook.progressPercent
                        )}
                        %
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={content.bookMetadata.userBook.progressPercent}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  )}

                  {/* Кнопки управления */}
                  <Stack
                    spacing={2}
                    sx={{ mb: 2 }}
                    alignItems={isMobile ? "stretch" : "flex-end"}
                  >
                    <Stack
                      direction={isMobile ? "column" : "row"}
                      spacing={2}
                      justifyContent={isMobile ? "stretch" : "flex-end"}
                    >
                      {arena?.isShareEnabled && (
                        <ShareButton
                          fullWidth={isMobile}
                          shareData={{
                            title: content.title,
                            url: content.shareUrl,
                          }}
                        />
                      )}
                      <SubscribeContentButton
                        content={content}
                        fullWidth={isMobile}
                      />
                    </Stack>

                    <Stack
                      direction={isMobile ? "column" : "row"}
                      spacing={2}
                      justifyContent={isMobile ? "stretch" : "flex-end"}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth={isMobile}
                        disabled={!content.access?.token}
                        onClick={() => handleReadIntent()}
                        startIcon={<VisibilityIcon />}
                      >
                        {content.bookMetadata?.userBook
                          ? t("book.continueReading", "Продолжить")
                          : t("book.read", "Читать")}
                      </Button>
                      {content.bookMetadata?.downloadUrl && (
                        <BookDownloadButton
                          url={content.bookMetadata.downloadUrl}
                          title={content.title}
                          fullWidth={isMobile}
                        />
                      )}
                      {content.bookMetadata?.tableOfContents &&
                        content.bookMetadata.tableOfContents.length > 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth={isMobile}
                            startIcon={<MenuBookIcon />}
                            onClick={() => setTocDialogOpen(true)}
                          >
                            {t("book.viewTableOfContents", "Оглавление")}
                          </Button>
                        )}
                    </Stack>
                  </Stack>
                </Grid>

                {/* Информация о платном контенте */}
                {content.products?.nodes?.length > 0 && !content.access?.token && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, p: 2 }}>
                      <ErrorScreen
                        title={t(
                          "paidContent.title",
                          "Доступ к контенту ограничен"
                        )}
                        text={t(
                          "paidContent.description",
                          "Доступ к контенту платный"
                        )}
                        icon={PaymentsIcon}
                        button={
                          <PaymentButtons
                            products={content.products.nodes.filter(
                              (product) => product.prices.length > 0
                            )}
                            referralContentId={content.id}
                            isTelegramArena={arena?.isTelegramArena}
                            onClickProductButton={dispatch}
                          />
                        }
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
          {content.products && (
            <ProductDialog
              referralContentId={content.id}
              products={content.products.nodes}
              state={state}
              dispatch={dispatch}
              refetch={refetch}
            />
          )}
          <BookTableOfContentsDialog
            open={tocDialogOpen}
            onClose={() => setTocDialogOpen(false)}
            tableOfContents={content.bookMetadata?.tableOfContents}
            bookTitle={content.title}
            contentId={contentId}
            hasAccess={!!content.access?.token}
            onReadRequest={handleReadIntent}
          />
          {content.ageRestrictions >= 18 && (
            <AgeConfirmationDialog
              open={ageConfirmOpen}
              onClose={() => {
                setAgeConfirmOpen(false);
                setPendingReadTarget(null);
              }}
              onConfirm={() => {
                const target = pendingReadTarget;
                setAgeConfirmOpen(false);
                setPendingReadTarget(null);
                navigateToReader(target);
              }}
              age={content.ageRestrictions}
            />
          )}
        </Container>
      )}
    </>
  );
}

Book.propTypes = {
  params: PropTypes.shape({
    contentId: PropTypes.string.isRequired,
  }),
};
